import { FC, TouchEvent } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/Tooltip'

// BookingType 인터페이스 정의
export interface BookingType {
  id: string
  startTime: number
  endTime: number
  title: string
  bookedBy?: string
  description?: string
}

interface TimelineViewProps {
  bookings: BookingType[]
  currentTime: number
}

export const TimelineView: FC<TimelineViewProps> = ({
  bookings,
  currentTime,
}) => {
  // Business hours: 9 AM to 6 PM (9 hours = 540 minutes)
  const startHour = 9
  const endHour = 18
  const totalMinutes = (endHour - startHour) * 60

  // Calculate position for current time indicator
  const currentTimePosition =
    ((currentTime - startHour * 60) / totalMinutes) * 100
  const isCurrentTimeVisible =
    currentTime >= startHour * 60 && currentTime <= endHour * 60

  // Format time (e.g., 9:30)
  const formatTime = (minutes: number, isMobile = false) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return isMobile
      ? `${hours}`
      : `${hours}:${mins.toString().padStart(2, '0')}`
  }

  // Add touch support for mobile devices
  const handleTouch = (event: TouchEvent, booking: BookingType) => {
    event.preventDefault()
    // You could implement additional touch-specific behavior here
    // For example, showing a modal on touch instead of tooltip
  }

  return (
    <div className="mt-4">
      <div className="text-sm font-medium mb-2">예약 타임라인</div>
      <div className="relative h-16 bg-gray-100 rounded-md hover:bg-gray-50 transition-colors duration-200">
        {/* Time labels */}
        <div className="absolute top-0 left-0 right-0 flex justify-between text-xs text-gray-500 px-2">
          {Array.from({ length: endHour - startHour + 1 }).map((_, index) => (
            <div key={index}>
              <span className="hidden sm:inline">{startHour + index}:00</span>
              <span className="sm:hidden">{startHour + index}</span>
            </div>
          ))}
        </div>

        {/* Current time indicator */}
        {isCurrentTimeVisible && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20"
            style={{ left: `${currentTimePosition}%` }}
          >
            <div className="absolute -top-5 -left-8 text-xs text-red-500 whitespace-nowrap">
              현재 시간
            </div>
          </div>
        )}

        {/* Bookings */}
        <TooltipProvider>
          {bookings.map((booking, index) => {
            const startPosition =
              ((booking.startTime - startHour * 60) / totalMinutes) * 100
            const duration = booking.endTime - booking.startTime
            const width = (duration / totalMinutes) * 100

            return (
              <Tooltip key={index}>
                <TooltipTrigger>
                  <div
                    className="absolute top-6 h-8 bg-blue-200 rounded-sm border border-blue-300 cursor-pointer hover:bg-blue-300 hover:shadow-md transition-all"
                    style={{
                      left: `${startPosition}%`,
                      width: `${width}%`,
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${booking.title} 예약 정보`}
                    onTouchStart={(e) => {
                      // Prevent default to avoid scrolling on mobile
                      e.stopPropagation()
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={5}>
                  <div className="font-medium text-base">{booking.title}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {formatTime(booking.startTime)} -{' '}
                    {formatTime(booking.endTime)}
                  </div>
                  <div className="text-sm mt-1">
                    예약자: {booking.bookedBy || '미지정'}
                  </div>
                  {booking.description && (
                    <div className="text-sm mt-1 text-gray-600">
                      {booking.description}
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </TooltipProvider>

        {/* Hour grid lines */}
        <div className="absolute inset-0 flex">
          {Array.from({ length: endHour - startHour }).map((_, index) => (
            <div
              key={index}
              className="h-full border-r border-gray-300 hover:bg-gray-200/30 transition-colors"
              style={{ width: `${100 / (endHour - startHour)}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
